"use client";

import { Clock, MapPin, Car, ArrowUpRight, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Location() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const today = new Date().getDay();
  const hours = [{
    day: "Monday",
    time: "9:00am – 5:00pm",
    dayIndex: 1
  }, {
    day: "Tuesday",
    time: "9:00am – 5:00pm",
    dayIndex: 2
  }, {
    day: "Wednesday",
    time: "9:00am – 5:00pm",
    dayIndex: 3
  }, {
    day: "Thursday",
    time: "9:00am – 5:00pm",
    dayIndex: 4
  }, {
    day: "Friday",
    time: "9:00am – 6:00pm",
    dayIndex: 5
  }, {
    day: "Saturday",
    time: "10:00am – 3:00pm",
    dayIndex: 6
  }, {
    day: "Sunday",
    time: "Closed",
    dayIndex: 0
  }];
  const address = "1965 Harbor Way, Ocean Springs, MS 39564";
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
  return <section data-section-id="906" id="location-hours" className="bg-[hsl(40,33%,96%)] py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
          {}
          <div ref={leftRef} className={`order-2 lg:order-1 flex flex-col justify-center transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="text-xs font-medium tracking-[0.25em] uppercase text-[hsl(215,60%,35%)] mb-5 font-default">
              Visit The Vessel
            </p>
            <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[48px] leading-[0.95] tracking-tighter text-[hsl(222,47%,11%)] mb-5">
              Find Our Dock.
            </h2>
            <p className="text-base md:text-lg text-[hsl(210,10%,45%)] leading-relaxed mb-10 max-w-lg font-default">
              Experience the heritage of The Miss Yvonne. Visit our market in Ocean Springs to select the finest wild-caught catch of the day.
            </p>

            {}
            <div className="pt-6 border-t border-[hsl(38,20%,85%)]">
              <p className="text-xs font-medium tracking-[0.25em] uppercase text-[hsl(215,60%,35%)] mb-3 font-default">
                Location
              </p>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[hsl(215,60%,35%)] mt-1 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-[17px] text-[hsl(222,47%,11%)] leading-relaxed font-default">
                    1965 Harbor Way
                    <br />
                    Ocean Springs, MS 39564
                  </p>
                  <Link to={directionsUrl} newTab className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-[hsl(215,60%,35%)] hover:text-[hsl(215,60%,35%)]/80 transition-colors group font-default">
                    Get directions
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.75} />
                  </Link>
                </div>
              </div>
            </div>

            {}
            <div className="pt-6 mt-6 border-t border-[hsl(38,20%,85%)]">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-medium tracking-[0.25em] uppercase text-[hsl(215,60%,35%)] font-default">
                  Market Hours
                </p>
                <Badge className="bg-[hsl(215,60%,35%)]/10 text-[hsl(215,60%,35%)] border-transparent px-2.5 py-1 text-xs font-medium hover:bg-[hsl(215,60%,35%)]/10">
                  <span className="inline-block w-1.5 h-1.5 bg-[hsl(215,60%,35%)] mr-1.5" />
                  Open Today
                </Badge>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[hsl(215,60%,35%)] mt-1 shrink-0" strokeWidth={1.5} />
                <ul className="flex-1 space-y-2.5">
                  {hours.map((h, i) => {
                  const isToday = h.dayIndex === today;
                  return <li data-index={i} key={h.day} className={`flex items-center justify-between text-[15px] font-default ${isToday ? "text-[hsl(222,47%,11%)] font-semibold" : "text-[hsl(210,10%,45%)]"}`}>
                        <span className="flex items-center gap-2">
                          {isToday && <span className="inline-block w-1.5 h-1.5 bg-[hsl(28,85%,58%)]" />}
                          {h.day}
                        </span>
                        <span className="tabular-nums">{h.time}</span>
                      </li>;
                })}
                </ul>
              </div>
            </div>

            {}
            <div className="pt-6 mt-6 border-t border-[hsl(38,20%,85%)]">
              <p className="text-xs font-medium tracking-[0.25em] uppercase text-[hsl(215,60%,35%)] mb-3 font-default">
                Docking
              </p>
              <div className="flex items-start gap-3">
                <Car className="w-5 h-5 text-[hsl(215,60%,35%)] mt-1 shrink-0" strokeWidth={1.5} />
                <p className="text-[15px] text-[hsl(210,10%,45%)] leading-relaxed font-default">
                  Ample parking is available directly at the harbor. Our market entrance is fully accessible for all visitors.
                </p>
              </div>
            </div>

            {}
            <div className="mt-10">
              <Button asChild size="lg" className="bg-[hsl(28,85%,58%)] text-white hover:bg-[hsl(222,47%,11%)] px-10 h-12 text-base font-medium shadow-md font-default">
                <Link to={directionsUrl} newTab>
                  Get Directions
                  <ArrowRight className="w-4 h-4 ml-1" strokeWidth={2} />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`order-1 lg:order-2 transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative h-[420px] sm:h-[520px] lg:h-full lg:min-h-[640px] overflow-hidden shadow-lg border border-[hsl(38,20%,85%)]">
              <iframe title="The Miss Yvonne Market location" src={mapEmbedUrl} className="absolute inset-0 w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />

              {}
              <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full">
                <div className="relative flex flex-col items-center">
                  <div className="relative flex items-center justify-center w-12 h-12 bg-[hsl(28,85%,58%)] shadow-lg ring-4 ring-[hsl(40,33%,96%)]">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor" aria-hidden="true">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                  </div>
                  <div className="w-3 h-3 rotate-45 bg-[hsl(28,85%,58%)] -mt-1.5 shadow-md" />
                </div>
              </div>

              {}
              <div className="absolute bottom-5 left-5 right-5 sm:right-auto sm:max-w-xs bg-[hsl(40,33%,96%)]/95 backdrop-blur-sm shadow-lg border border-[hsl(38,20%,85%)] p-4">
                <p className="font-serif font-default text-lg text-[hsl(222,47%,11%)] leading-tight mb-1">
                  The Miss Yvonne Market
                </p>
                <p className="text-xs text-[hsl(210,10%,45%)] mb-2.5 leading-relaxed font-default">
                  Ocean Springs, Mississippi
                </p>
                <Link to={mapsUrl} newTab className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(215,60%,35%)] hover:text-[hsl(215,60%,35%)]/80 transition-colors group font-default">
                  View on Maps
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}